// BL-39 — the left column of the staff modal: who they are and how they are set
// up. One "Edit" menu lists the fields this viewer may change; each item opens
// the single-field dialog the Roster's ⋯ menu opens, so a change is still one
// field at a time.
//
// Three fields are shown and never editable here, on purpose:
//   · Therapist Code — permanent (0033 / OQ-11). It is printed, shared with
//     clients and used to book a specific therapist; a changed code breaks
//     every place it was given out.
//   · Email — the sign-in identity in GoTrue. Editing the copy on `staff`
//     would leave the two disagreeing.
//   · Commission rate — shown to admin/superadmin only. Who may change a
//     person's pay rate is not decided anywhere, so there is no control for it.

import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bednbody/ui/components/dropdown-menu";
import { cn } from "@bednbody/ui/lib/utils";
import {
  CameraIcon,
  CheckCircleIcon,
  EnvelopeSimpleIcon,
  HashIcon,
  type Icon,
  MedalIcon,
  MinusCircleIcon,
  MotorcycleIcon,
  PencilSimpleIcon,
  PercentIcon,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { CopyableCode } from "@/components/CopyableCode";
import { InitialsAvatar } from "../../live-map/LiveMapCards";
import type { RiderVehicle } from "../../staff-media/staff-media.hooks";
import { rankLabel, roleLabel, type StaffEditKind } from "../components/StaffEditDialogs";
import type { RosterPerson } from "../components/types";
import type { StaffAccess } from "./staff-detail.access";
import { useStaffCommissionRate } from "./staff-detail.hooks";

interface DetailRow {
  icon: Icon;
  label: string;
  value: ReactNode;
  /** Why it cannot be edited, when that is a rule rather than a permission. */
  note?: string;
}

function editItems(
  person: RosterPerson,
  access: StaffAccess,
  vehicle: RiderVehicle | null,
): { kind: StaffEditKind; label: string }[] {
  return [
    access.canEditName ? { kind: "name" as const, label: "Edit name" } : null,
    access.canChangeRole ? { kind: "role" as const, label: "Change role" } : null,
    access.canReassignHub ? { kind: "hub" as const, label: "Reassign hub" } : null,
    access.canSetRiderRank ? { kind: "rider-rank" as const, label: "Change rank" } : null,
    access.canSetTherapistRank ? { kind: "therapist-rank" as const, label: "Change rank" } : null,
    access.canSetPlate
      ? { kind: "plate" as const, label: vehicle ? "Change plate" : "Set plate" }
      : null,
    access.canToggleActive
      ? { kind: "toggle" as const, label: person.active ? "Deactivate" : "Activate" }
      : null,
    access.canResetPassword ? { kind: "reset-password" as const, label: "Reset password" } : null,
  ].filter((item) => item !== null);
}

/** The small uppercase label the console uses over every group of facts. */
function SectionLabel({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "primary";
}) {
  return (
    <p
      className={cn(
        "text-xs font-semibold uppercase tracking-wider",
        tone === "primary" ? "text-primary" : "text-muted-foreground",
      )}
    >
      {children}
    </p>
  );
}

export function StaffProfileCard({
  person,
  access,
  photo,
  vehicle,
  liveStatus,
  onEdit,
}: {
  person: RosterPerson;
  access: StaffAccess;
  photo: string | null;
  vehicle: RiderVehicle | null;
  /** The status engine's pill, or `null` for staff who work no sessions. */
  liveStatus: ReactNode;
  onEdit: (kind: StaffEditKind) => void;
}) {
  const { data: commissionRate } = useStaffCommissionRate(
    person.id,
    access.canSeeMoney && person.role === "therapist",
  );
  const edits = editItems(person, access, vehicle);

  const rows: DetailRow[] = [
    {
      icon: EnvelopeSimpleIcon,
      label: "Email",
      value: person.email,
      note: "Their sign-in. It can't be changed here.",
    },
    ...(person.role === "therapist"
      ? [
          {
            icon: HashIcon,
            label: "Therapist Code",
            value: person.therapist_code ? (
              <CopyableCode label="Therapist Code" value={person.therapist_code} />
            ) : (
              "Not issued"
            ),
            note: "Permanent. Clients use it to book this therapist.",
          },
          {
            icon: MedalIcon,
            label: "Rank",
            value: rankLabel(person.therapist_rank),
            note: person.rank_is_manual ? "Set by hand" : "Set by the monthly system",
          },
        ]
      : []),
    ...(person.role === "rider"
      ? [
          {
            icon: MedalIcon,
            label: "Rank",
            value: rankLabel(person.rider_rank),
            note: person.rank_is_manual ? "Set by hand" : "Set by the monthly system",
          },
          {
            icon: MotorcycleIcon,
            label: "Plate",
            value: vehicle ? (
              <span>
                <span className="font-mono">{vehicle.plate}</span>
                <span className="text-muted-foreground"> · {vehicle.vehicleType}</span>
              </span>
            ) : (
              "Not set yet"
            ),
          },
        ]
      : []),
    ...(access.canSeeMoney && person.role === "therapist"
      ? [
          {
            icon: PercentIcon,
            label: "Commission rate",
            value:
              commissionRate === undefined || commissionRate === null
                ? "—"
                : `${Math.round(commissionRate * 1000) / 10}%`,
          },
        ]
      : []),
    {
      icon: CameraIcon,
      label: "Photo",
      value: photo ? "On file" : "Not uploaded yet",
      note: "They add it themselves in the partner app.",
    },
  ];

  return (
    <section className="rounded-xl border border-border bg-card shadow-xs">
      <div className="flex items-start justify-between gap-3 border-b border-border p-5">
        <div className="min-w-0 space-y-0.5">
          <SectionLabel tone="primary">Staff profile</SectionLabel>
          <p className="text-xs text-muted-foreground">Identity and access</p>
        </div>
        {edits.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  aria-label={`Edit ${person.name}`}
                  shape="soft"
                  size="sm"
                  variant="outline"
                />
              }
            >
              <PencilSimpleIcon data-icon="inline-start" />
              Edit
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {edits.map((item) => (
                <DropdownMenuItem key={item.kind} onClick={() => onEdit(item.kind)}>
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <div className="space-y-5 p-5">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <InitialsAvatar name={person.name} photo={photo} />
          <div className="min-w-0 space-y-1.5">
            <p className="truncate font-semibold text-foreground">{person.name}</p>
            <div className="flex flex-wrap items-center gap-2">
              <Badge shape="soft" variant={person.active ? "success-soft" : "warning-soft"}>
                {person.active ? (
                  <CheckCircleIcon weight="bold" />
                ) : (
                  <MinusCircleIcon weight="bold" />
                )}
                {person.active ? "Active" : "Inactive"}
              </Badge>
              {liveStatus}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0 rounded-lg border border-border p-3">
            <SectionLabel>Role</SectionLabel>
            <p className="mt-1 truncate text-sm font-semibold capitalize text-foreground">
              {roleLabel(person.role)}
            </p>
          </div>
          <div className="min-w-0 rounded-lg border border-border p-3">
            <SectionLabel>Hub</SectionLabel>
            <p className="mt-1 truncate text-sm font-semibold text-foreground">
              {person.hubName ?? "No hub"}
            </p>
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-5">
          <SectionLabel>Account</SectionLabel>
          <dl className="space-y-4">
            {rows.map(({ icon: RowIcon, label, value, note }) => (
              <div key={label}>
                <dt className="flex items-center gap-2 text-xs text-muted-foreground">
                  <RowIcon aria-hidden className="size-4 shrink-0" />
                  {label}
                </dt>
                <dd className="mt-0.5 min-w-0 pl-6">
                  <div className="truncate text-sm font-medium text-foreground">{value}</div>
                  {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
